/* eslint-disable react/require-default-props */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 * @deprecated This component is deprecated and will be removed in a future release.
 * Please use the new Quimby UI component instead:
 *
 * Import: `import { InlineText } from '@ui/molecules/inputs';`
 * Or: `import { InlineText } from '@ui/molecules/inputs/inline';`
 *
 * Or specific path: `import { InlineText } from '@ui/molecules/inputs/inline/InlineText';`
 *
 * Use with variant="number" for number inputs:
 * `<InlineText variant="number" numberValue={value} onNumberChange={handleChange} />`
 *
 * See the new component for documentation and migration notes.
 */
import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import { truncateNumber } from '@utils/functions/number.functions';
export function InlineEditNumber(_a) {
    var { as, children, className, disableEdit = false, placeholder = '--', label, id, 'aria-labelledby': ariaLabelledby, numberValue, onNumberChange, allowDecimals = true, allowNegative = true, maxLength } = _a, props = __rest(_a, ["as", "children", "className", "disableEdit", "placeholder", "label", "id", 'aria-labelledby', "numberValue", "onNumberChange", "allowDecimals", "allowNegative", "maxLength"]);
    const Component = as || 'div';
    const variantClass = 'qmb-inedit--number';
    const contentRef = useRef(null);
    const formatNumber = (value) => {
        if (value === undefined || value === null || value === '')
            return '';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return Number.isNaN(numValue) ? '' : numValue.toString();
    };
    const validateInput = (input) => {
        let cleaned = input;
        // Remove all non-numeric characters except decimal point and minus
        if (allowDecimals && allowNegative) {
            cleaned = cleaned.replace(/[^0-9.-]/g, '');
        }
        else if (allowDecimals) {
            cleaned = cleaned.replace(/[^0-9.]/g, '');
        }
        else if (allowNegative) {
            cleaned = cleaned.replace(/[^0-9-]/g, '');
        }
        else {
            cleaned = cleaned.replace(/[^0-9]/g, '');
        }
        // Handle multiple decimal points - keep only the first one
        if (allowDecimals) {
            const parts = cleaned.split('.');
            if (parts.length > 2) {
                cleaned = `${parts[0]}.${parts.slice(1).join('')}`;
            }
        }
        // Handle multiple minus signs - keep only the first one if it's at the start
        if (allowNegative) {
            const minusCount = (cleaned.match(/-/g) || []).length;
            if (minusCount > 1) {
                const firstMinus = cleaned.indexOf('-') === 0 ? '-' : '';
                cleaned = firstMinus + cleaned.replace(/-/g, '');
            }
            else if (cleaned.indexOf('-') > 0) {
                // Minus sign should only be at the beginning
                cleaned = cleaned.replace(/-/g, '');
            }
        }
        return truncateNumber(cleaned, maxLength);
    };
    const handleInput = (e) => {
        if (disableEdit)
            return;
        const target = e.currentTarget;
        const inputValue = target.textContent || '';
        const validatedValue = validateInput(inputValue);
        // Update the display if input was cleaned
        if (validatedValue !== inputValue) {
            target.textContent = validatedValue;
            const range = document.createRange();
            const selection = window.getSelection();
            if (selection) {
                range.selectNodeContents(target);
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    };
    const handleBlur = (e) => {
        if (disableEdit)
            return;
        const value = e.currentTarget.textContent || '';
        const trimmedValue = value.trim();
        if (trimmedValue === '') {
            onNumberChange === null || onNumberChange === void 0 ? void 0 : onNumberChange('');
            return;
        }
        const numericValue = parseFloat(trimmedValue);
        if (!Number.isNaN(numericValue)) {
            onNumberChange === null || onNumberChange === void 0 ? void 0 : onNumberChange(numericValue);
        }
        else if (contentRef.current) {
            contentRef.current.textContent = formatNumber(numberValue);
        }
    };
    const handleKeyDown = (e) => {
        var _a, _b;
        if (disableEdit)
            return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            (_a = e.currentTarget) === null || _a === void 0 ? void 0 : _a.blur();
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            // Reset to original value on escape
            if (contentRef.current) {
                contentRef.current.textContent = formatNumber(numberValue);
            }
            (_b = e.currentTarget) === null || _b === void 0 ? void 0 : _b.blur();
        }
    };
    // Update content when numberValue changes externally
    useEffect(() => {
        if (contentRef.current && document.activeElement !== contentRef.current) {
            contentRef.current.textContent = formatNumber(numberValue);
        }
    }, [numberValue]);
    const displayValue = formatNumber(numberValue) || children || placeholder;
    return (React.createElement(Component, Object.assign({ ref: contentRef, className: classNames({ [variantClass]: !disableEdit }, className), contentEditable: !disableEdit, suppressContentEditableWarning: !disableEdit, "data-placeholder": placeholder, "data-label": label, role: "textbox", "aria-multiline": false, inputMode: allowDecimals ? 'decimal' : 'numeric', "aria-label": ariaLabelledby ? undefined : label, spellCheck: false, autoCorrect: "off", autoCapitalize: "off", enterKeyHint: "done", id: id, "aria-labelledby": ariaLabelledby }, props, { onInput: handleInput, onBlur: handleBlur, onKeyDown: handleKeyDown }), displayValue));
}
